import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Eye } from "lucide-react";
import { toast } from "sonner";
import { inr, lineById } from "@/data/products";
import RivetCorners from "@/components/denim/RivetCorners";
import { useWishlist } from "@/components/denim/WishlistContext";

export default function ProductCard({ p, onQuickView, index = 0 }) {
    const pct = Math.round((1 - p.now / p.was) * 100);
    const line = lineById(p.line);
    const hasSecond = Boolean(p.img2 && p.img2 !== p.img);
    const { has, toggle } = useWishlist();
    const saved = has(p.id);
    return (
        <motion.article
            data-testid={`product-card-${p.id}`}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
            className="group"
        >
            <div className="relative aspect-[3/4] overflow-hidden bg-elev">
                <RivetCorners inset="8px" />
                <img
                    src={p.img}
                    alt={p.name}
                    loading="lazy"
                    className={`absolute inset-0 h-full w-full object-cover transition-all duration-700 ease-out group-hover:scale-105 ${
                        hasSecond ? "group-hover:opacity-0" : ""
                    }`}
                />
                {hasSecond ? (
                    <img
                        src={p.img2}
                        alt=""
                        loading="lazy"
                        aria-hidden="true"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-0 transition-all duration-700 ease-out group-hover:scale-100 group-hover:opacity-100"
                    />
                ) : (
                    <img
                        src={p.img}
                        alt=""
                        loading="lazy"
                        aria-hidden="true"
                        className="absolute inset-0 h-full w-full object-cover object-top scale-[1.6] opacity-0 transition-all duration-700 ease-out group-hover:opacity-100"
                    />
                )}
                <Link
                    to={`/product/${p.id}`}
                    data-testid={`product-link-${p.id}`}
                    aria-label={`View ${p.name}`}
                    className="absolute inset-0 z-[5]"
                />
                {pct > 0 && (
                    <span className="absolute left-0 top-4 z-10 bg-gold px-3 py-1 font-mono text-[11px] font-medium text-black">
                        {pct}% OFF
                    </span>
                )}
                <div className="absolute right-3 top-3 z-10 flex translate-x-14 flex-col gap-2 opacity-0 transition-all duration-500 group-hover:translate-x-0 group-hover:opacity-100">
                    <button
                        data-testid={`wishlist-button-${p.id}`}
                        aria-label={saved ? "Remove from wishlist" : "Add to wishlist"}
                        onClick={() => {
                            const added = toggle({ productId: p.id, name: p.name, price: p.now, img: p.img });
                            toast[added ? "success" : "info"](
                                added ? `${p.name} saved to your wishlist` : `${p.name} removed from wishlist`,
                            );
                        }}
                        className={`grid h-10 w-10 place-items-center border backdrop-blur transition-colors ${
                            saved
                                ? "border-gold bg-gold text-black"
                                : "border-white/15 bg-black/60 text-ink hover:bg-gold hover:text-black"
                        }`}
                    >
                        <Heart size={16} fill={saved ? "currentColor" : "none"} />
                    </button>
                    <button
                        data-testid={`quickview-button-${p.id}`}
                        aria-label="Quick view"
                        onClick={() => onQuickView && onQuickView(p)}
                        className="grid h-10 w-10 place-items-center border border-white/15 bg-black/60 text-ink backdrop-blur transition-colors hover:bg-gold hover:text-black"
                    >
                        <Eye size={16} />
                    </button>
                </div>
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/60 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                <span className="pointer-events-none absolute bottom-3 left-3 z-10 translate-y-3 font-mono text-[10px] uppercase tracking-[0.25em] text-ink opacity-0 transition-all duration-500 group-hover:translate-y-0 group-hover:opacity-100">
                    Limited Edition
                </span>
            </div>
            <div className="flex items-start justify-between gap-3 pt-4">
                <div>
                    <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-mute">
                        {line ? line.name : "URWAN"} · {p.cat === "pants" ? "Pants" : "Shirts"}
                    </p>
                    <h3 className="mt-1 font-display text-base font-semibold leading-snug text-ink transition-colors duration-300 group-hover:text-gold md:text-lg">
                        <Link to={`/product/${p.id}`} data-testid={`product-title-link-${p.id}`}>
                            {p.name}
                        </Link>
                    </h3>
                </div>
                <div className="shrink-0 text-right">
                    <p className="font-display text-base font-semibold text-gold md:text-lg">
                        {inr(p.now)}
                    </p>
                    {p.now < p.was && (
                        <p className="text-xs text-mute line-through">{inr(p.was)}</p>
                    )}
                </div>
            </div>
        </motion.article>
    );
}
